import React from "react";
import SuiBox from "components/SuiBox";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import ErrorAlert from "components/ErrorAlert";
import CustomSelect from "components/CustomSelect";
import MonthPicker from "components/MonthPicker";
import { Card, CircularProgress, Grid, Icon } from "@mui/material";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import moment from "moment";
import {
  getUsersReq,
  getUnresolvedCommentsReportReq,
  resolveAttendanceCommentReq,
  updateAttendanceDateTimeReq,
} from "services/api";
import TimelineItem from "examples/Timeline/TimelineItem";
import { CHECKED_IN_TYPES, TIME_FORMAT } from "constants";
import {
  customSelectStyles,
  REPORT_BUTTON_SX,
  REPORT_TOOLBAR_SX,
} from "../../reports.constants";
import { toHoursAndMinutes, minsDiff } from "utils/DateAndTime";
import Pagination from "utils/Pagination";

// Let the control grow with wrapped chips instead of overflowing the fixed 35px height
const multiSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...customSelectStyles.control(provided, state),
    height: "auto",
    minHeight: "35px",
  }),
  valueContainer: (provided) => ({
    ...customSelectStyles.valueContainer(provided),
    padding: "2px 8px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: "flex-start",
  }),
};

const rangeValues = [
  { label: "All Time", value: "all" },
  { label: "Last 7 days", value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 60 days", value: 60 },
  { label: "Last 90 days", value: 90 },
  { label: "Custom (Month)", value: "custom" },
];

const pageLimit = 20;

const buildRangeParams = (range, month) => {
  if (!range || range.value === "all") return {};
  const now = moment();
  if (range.value === "custom") {
    const fromMoment = moment(month, "YYYY-MM").startOf("month");
    const monthEnd = moment(month, "YYYY-MM").endOf("month");
    const toMoment = monthEnd.isAfter(now) ? now.clone() : monthEnd;
    return { from: fromMoment.unix(), to: toMoment.unix() };
  }
  return { from: now.clone().subtract(range.value, "days").unix(), to: now.unix() };
};

function UnresolvedComments() {
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRange, setSelectedRange] = useState(rangeValues[0]);
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [reportData, setReportData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Store
  const {
    auth: { admin, team, channel },
  } = useSelector((state) => state);

  const getUsers = async () => {
    setIsLoading(true);
    try {
      const admin_id = admin.id;
      const { data } = await getUsersReq({
        admin_id,
        team_id: team.id,
        channel_id: channel.id,
      });
      setUsers(data);
    } catch (err) {
      setErrorMessage(err.response?.data?.message);
    }
    setIsLoading(false);
  };

  const fetchUnresolvedComments = async (overrides = {}) => {
    const usersFilter =
      overrides.selectedUsers !== undefined ? overrides.selectedUsers : selectedUsers;
    const range = overrides.range || selectedRange;
    const month = overrides.month !== undefined ? overrides.month : selectedMonth;
    const page = overrides.page || currentPage;
    const silent = overrides.silent || false;

    if (range?.value === "custom" && !month) return;

    if (!silent) setIsReportLoading(true);
    try {
      const admin_id = admin.id;
      const body = {
        ...buildRangeParams(range, month),
        pageNumber: page,
        pageLimit,
        ...(usersFilter.length > 0 ? { user_slack_ids: usersFilter.map((user) => user.value) } : {}),
      };
      const { data } = await getUnresolvedCommentsReportReq({
        admin_id,
        team_id: team.id,
        channel_id: channel.id,
        body,
      });
      setReportData(Array.isArray(data.report) ? data.report : []);
      setTotalCount(data.total || 0);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to load unresolved comments");
    }
    if (!silent) setIsReportLoading(false);
  };

  const updateAttendanceTime = async ({ recordId, field, newDateTime, breakId }) => {
    try {
      const updatedTimestamp = moment(newDateTime).unix();
      const adminId = admin.id;

      await updateAttendanceDateTimeReq({
        admin_id: adminId,
        record_id: recordId,
        field,
        timestamp: updatedTimestamp,
        break_id: breakId,
      });

      return updatedTimestamp;
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to update attendance");
      throw err;
    }
  };

  const resolveComment = async ({ recordId, field, breakId }) => {
    try {
      const adminId = admin.id;
      await resolveAttendanceCommentReq({
        admin_id: adminId,
        record_id: recordId,
        field,
        break_id: breakId,
      });
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to resolve comment");
      throw err;
    }
  };

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUnresolvedComments({ page: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const generateReport = () => {
    setCurrentPage(1);
    fetchUnresolvedComments({ page: 1 });
  };

  return (
    <>
      {errorMessage && (
        <ErrorAlert color="error" message={errorMessage} clearError={() => setErrorMessage(null)} />
      )}
      <SuiBox py={3}>
        <SuiBox display="flex" alignItems="center" flexWrap="wrap" sx={REPORT_TOOLBAR_SX}>
          <SuiBox className="w-340" sx={{ minHeight: "35px" }}>
            {isLoading ? (
              <CircularProgress color="dark" size={18} />
            ) : (
              <CustomSelect
                isMulti
                isSearchable={true}
                customStyles={multiSelectStyles}
                options={users}
                value={selectedUsers}
                placeholder="All Engineers"
                onChange={(data) => setSelectedUsers(data || [])}
              />
            )}
          </SuiBox>
          <SuiBox sx={{ height: "35px" }}>
            <CustomSelect
              customStyles={customSelectStyles}
              options={rangeValues}
              value={selectedRange}
              placeholder="Select Days"
              onChange={(data) => setSelectedRange(data)}
            />
          </SuiBox>
          {selectedRange?.value === "custom" && (
            <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
          )}
          <SuiButton
            variant="gradient"
            color="secondary"
            size="small"
            onClick={generateReport}
            sx={REPORT_BUTTON_SX}
          >
            Generate Report
            <Icon sx={{ fontWeight: "bold" }}>arrow_forward</Icon>
          </SuiButton>
        </SuiBox>
      </SuiBox>

      <SuiBox pb={3}>
        <Grid container spacing={3}>
          {isReportLoading ? (
            <Grid item xs={12} md={12} lg={12}>
              <Card className="h-100">
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <CircularProgress color="dark" size={30} />
                </SuiBox>
              </Card>
            </Grid>
          ) : reportData.length > 0 ? (
            reportData.map((dayEntry, index) => {
              let start = 0;
              let duration = 0;
              return (
                <Grid item xs={12} md={6} lg={4} key={`main-${index}`}>
                  <Card className="h-100">
                    <SuiBox pt={3} px={3}>
                      <SuiTypography variant="h6" fontWeight="medium">
                        {moment(dayEntry.day, "L").format("LL")}
                      </SuiTypography>
                      <SuiTypography variant="caption" fontWeight="regular" color="text">
                        {dayEntry.name} ({dayEntry.user_name})
                      </SuiTypography>
                    </SuiBox>
                    <SuiBox p={2} className="report-card-fix-height">
                      {dayEntry.records.map((record, recordIndex) => {
                        if (recordIndex === 0 || record.from) {
                          start = record.from;
                          duration = minsDiff(record.from, start);
                        } else {
                          if (record.break_from) {
                            duration += minsDiff(record.break_from, start);
                            start = record.break_from;
                          }
                          if (record.break_to) {
                            start = record.break_to;
                          }
                          if (record.to) {
                            duration += minsDiff(record.to, start);
                            start = record.to;
                          }
                        }

                        return (
                          <React.Fragment key={`record-${recordIndex}`}>
                            {record.from && (
                              <TimelineItem
                                color={`${
                                  record.type === CHECKED_IN_TYPES.wfh ? "primary" : "success"
                                }`}
                                icon={`${
                                  record.type === CHECKED_IN_TYPES.wfh ? "home_work" : "business"
                                }`}
                                title={`${recordIndex ? "Checked-In-Again" : "Checked-In"} (${
                                  record.type
                                })`}
                                dateTime={moment.unix(record.from).format(TIME_FORMAT)}
                                comment={record.from_comment}
                                recordTimestamp={record.from}
                                isUnresolvedComment={record.is_unresolved_comment}
                                onResolve={
                                  record.is_unresolved_comment
                                    ? async () => {
                                        await resolveComment({ recordId: record.id, field: "from" });
                                        fetchUnresolvedComments({ page: currentPage, silent: true });
                                      }
                                    : null
                                }
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "from",
                                      newDateTime,
                                    });
                                    fetchUnresolvedComments({ page: currentPage, silent: true });
                                  } catch (err) {
                                    // errorMessage already set inside updateAttendanceTime
                                  }
                                }}
                              />
                            )}
                            {record.break_from && (
                              <TimelineItem
                                color="warning"
                                icon="cookie"
                                title="Break"
                                dateTime={moment.unix(record.break_from).format(TIME_FORMAT)}
                                recordTimestamp={record.break_from}
                                comment={record.break_from_comment}
                                isUnresolvedComment={record.is_unresolved_comment}
                                onResolve={
                                  record.is_unresolved_comment
                                    ? async () => {
                                        await resolveComment({
                                          recordId: record.id,
                                          field: "break_from",
                                          breakId: record.break_id,
                                        });
                                        fetchUnresolvedComments({ page: currentPage, silent: true });
                                      }
                                    : null
                                }
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "break_from",
                                      newDateTime,
                                      breakId: record.break_id,
                                    });
                                    fetchUnresolvedComments({ page: currentPage, silent: true });
                                  } catch (err) {
                                    // errorMessage already set inside updateAttendanceTime
                                  }
                                }}
                              />
                            )}
                            {record.break_to && (
                              <TimelineItem
                                color="secondary"
                                icon="reply"
                                title="Back"
                                dateTime={moment.unix(record.break_to).format(TIME_FORMAT)}
                                recordTimestamp={record.break_to}
                                comment={record.break_to_comment}
                                isUnresolvedComment={record.is_unresolved_comment}
                                onResolve={
                                  record.is_unresolved_comment
                                    ? async () => {
                                        await resolveComment({
                                          recordId: record.id,
                                          field: "break_to",
                                          breakId: record.break_id,
                                        });
                                        fetchUnresolvedComments({ page: currentPage, silent: true });
                                      }
                                    : null
                                }
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "break_to",
                                      newDateTime,
                                      breakId: record.break_id,
                                    });
                                    fetchUnresolvedComments({ page: currentPage, silent: true });
                                  } catch (err) {
                                    // errorMessage already set inside updateAttendanceTime
                                  }
                                }}
                              />
                            )}
                            {record.to && (
                              <TimelineItem
                                color="dark"
                                icon="logout"
                                title={`Checked-Out (${record.type})`}
                                dateTime={moment.unix(record.to).format(TIME_FORMAT)}
                                recordTimestamp={record.to}
                                comment={record.to_comment}
                                description={`Duration: ${toHoursAndMinutes(duration)}`}
                                isUnresolvedComment={record.is_unresolved_comment}
                                onResolve={
                                  record.is_unresolved_comment
                                    ? async () => {
                                        await resolveComment({ recordId: record.id, field: "to" });
                                        fetchUnresolvedComments({ page: currentPage, silent: true });
                                      }
                                    : null
                                }
                                onDateTimeUpdate={async (newDateTime) => {
                                  try {
                                    await updateAttendanceTime({
                                      recordId: record.id,
                                      field: "to",
                                      newDateTime,
                                    });
                                    fetchUnresolvedComments({ page: currentPage, silent: true });
                                  } catch (err) {
                                    // errorMessage already set inside updateAttendanceTime
                                  }
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </SuiBox>
                  </Card>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12} md={12} lg={12}>
              <Card className="h-100">
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <Icon fontSize="large" color="success" className="cursor-pointer">
                    task_alt
                  </Icon>
                  <SuiTypography
                    component="span"
                    variant="button"
                    fontWeight="regular"
                    textTransform="capitalize"
                    color={"dark"}
                    opacity={0.5}
                    m={3}
                    sx={{ lineHeight: 0 }}
                  >
                    No unresolved comments
                  </SuiTypography>
                </SuiBox>
              </Card>
            </Grid>
          )}
        </Grid>
        {!isReportLoading && totalCount > pageLimit && (
          <SuiBox mt={3}>
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={totalCount}
              pageSize={pageLimit}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </SuiBox>
        )}
      </SuiBox>
    </>
  );
}

export default UnresolvedComments;
